import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { ButtonLoadable } from '@/components/ui/button-loadable';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

interface IntegrationDisconnectConfirmDialogProps {
  open: boolean;
  /** Display name of the integration the entry is linked to (e.g. "Jira"). */
  integrationName: string;
  isLoading: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

/**
 * Confirms disconnecting a time entry from its integration before saving an edit.
 *
 * Rendered as a plain sibling `Dialog` rather than the shared `ConfirmationModal`
 * (overhead/assignment-modal pattern): `ConfirmationModal` renders UNDER the parent
 * dialog's overlay, so only the backdrop would darken and the confirm itself would
 * be invisible. Radix stacks sibling dialogs correctly.
 */
export function IntegrationDisconnectConfirmDialog({
  open,
  integrationName,
  isLoading,
  onConfirm,
  onCancel,
}: IntegrationDisconnectConfirmDialogProps) {
  const { t } = useTranslation(['authenticated.timeTracking', 'common']);

  return (
    <Dialog open={open} onOpenChange={isOpen => !isOpen && onCancel()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {t('modal.addEntry.integrationDisconnectConfirm.title', {
              integration: integrationName,
            })}
          </DialogTitle>
        </DialogHeader>
        <DialogDescription>
          {t('modal.addEntry.integrationDisconnectConfirm.message', {
            integration: integrationName,
          })}
        </DialogDescription>
        <DialogFooter>
          <Button variant="outline" onClick={onCancel}>
            {t('common:cancel')}
          </Button>
          <ButtonLoadable isLoading={isLoading} onClick={onConfirm}>
            {t('modal.addEntry.integrationDisconnectConfirm.confirmText')}
          </ButtonLoadable>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
